import { useCallback } from 'react';

import {
  Currency,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useCryptoAmount } from '@sorare/core/src/hooks/useCryptoAmount';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';

type ReturnedProps = {
  formatPrice: (value: number, maxReachesEnd?: boolean) => string;
  sliderToPrice: (value: number) => string;
  priceToSlider: (value: number) => number;
};

export const useFormatFloorPricesFilters = (): ReturnedProps => {
  const { currency, fiatCurrency, cryptocurrency } = useCurrentUserCurrencies();
  const {
    toMonetaryAmount,
    toMonetaryAmountFromMajorMainCurrency,
    getUserAmount,
  } = useMonetaryAmount();
  const { formatCryptoMajor } = useCryptoAmount(cryptocurrency);
  const { formatNumber } = useIntlContext();

  const sliderToPrice = useCallback(
    (value: number): string => {
      const monetaryAmount = toMonetaryAmount({
        referenceCurrency: SupportedCurrency.EUR,
        eurCents: value,
      });
      return getUserAmount(monetaryAmount).toFixed(2);
    },
    [toMonetaryAmount, getUserAmount]
  );

  const formatPrice = useCallback(
    (value: number, maxReachesEnd?: boolean) => {
      const price = sliderToPrice(value);

      const formattedNumber = (() => {
        if (currency === Currency.FIAT) {
          return formatNumber(Number(price), {
            style: 'currency',
            currency: fiatCurrency,
          });
        }
        return formatCryptoMajor(price);
      })();

      return `${formattedNumber}${maxReachesEnd ? '+' : ''}`;
    },
    [currency, fiatCurrency, formatCryptoMajor, formatNumber, sliderToPrice]
  );

  const priceToSlider = useCallback(
    (value: number): number => {
      return toMonetaryAmountFromMajorMainCurrency(value).eurCents;
    },
    [toMonetaryAmountFromMajorMainCurrency]
  );

  return {
    formatPrice,
    sliderToPrice,
    priceToSlider,
  };
};
